import dayjs from 'dayjs';
import { ChevronDown, EllipsisVertical } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from 'cn';
import type {
  CustomEventAction,
  CustomEventActionSeverity,
} from 'hooks/customEvents/useCustomEvents';

const SEVERITY_BAR_CLASS: Record<CustomEventActionSeverity, string> = {
  low: 'tw:bg-green-500',
  normal: 'tw:bg-neutral-400',
  high: 'tw:bg-orange-500',
  critical: 'tw:bg-red-600',
};

const SEVERITY_LABEL: Record<CustomEventActionSeverity, string> = {
  low: 'Low severity',
  normal: 'Normal severity',
  high: 'High severity',
  critical: 'Critical severity',
};

const formatActionDate = (value: string | null) =>
  value ? dayjs(value).format('MMM D, YYYY, h:mm A') : null;

type EventActionRowProps = {
  action: CustomEventAction;
  onEdit: (action: CustomEventAction) => void;
  onRemove: (action: CustomEventAction) => void;
};

const EventActionRow: React.FC<EventActionRowProps> = ({
  action,
  onEdit,
  onRemove,
}) => {
  const isClosed = action.status === 'Closed';
  const barClass = action.severity
    ? SEVERITY_BAR_CLASS[action.severity]
    : 'tw:bg-neutral-400';
  const startDate = formatActionDate(action.startDate);
  const targetDate = formatActionDate(action.targetDate);
  const checklist = action.checklist ?? [];

  return (
    <Collapsible className="tw:flex tw:overflow-hidden tw:rounded-md tw:border tw:border-border">
      <div className={cn('tw:w-1 tw:shrink-0', barClass)} aria-hidden />
      <div className="tw:min-w-0 tw:flex-1">
        <div className="tw:flex tw:items-center tw:gap-2 tw:py-2 tw:pr-1 tw:pl-3">
          <div className="tw:min-w-0 tw:flex-1">
            <div
              className={cn(
                'tw:truncate tw:font-medium',
                isClosed && 'tw:text-muted-foreground tw:line-through'
              )}
            >
              {action.name}
            </div>
            <div className="tw:truncate tw:text-xs tw:text-muted-foreground">
              {[
                action.severity && SEVERITY_LABEL[action.severity],
                action.pocUserName ?? 'Unassigned',
              ]
                .filter(Boolean)
                .join(' · ')}
            </div>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger
              render={
                <Button
                  variant="ghost"
                  size="icon-sm"
                  aria-label={`${action.name} options`}
                />
              }
            >
              <EllipsisVertical />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => onEdit(action)}>
                Edit
              </DropdownMenuItem>
              <DropdownMenuItem
                variant="destructive"
                onClick={() => onRemove(action)}
              >
                Remove
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <CollapsibleTrigger
            render={
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label={`Toggle ${action.name} details`}
              />
            }
          >
            <ChevronDown className="tw:transition-transform tw:group-data-panel-open/button:rotate-180" />
          </CollapsibleTrigger>
        </div>
        <CollapsibleContent className="tw:grid tw:gap-2 tw:px-3 tw:pb-3 tw:text-sm">
          {action.description && <div>{action.description}</div>}
          {checklist.length > 0 && (
            <ul className="tw:m-0 tw:grid tw:list-none tw:gap-1 tw:p-0">
              {checklist.map((item, index) => (
                <li key={`${item.label}-${index}`}>
                  <label
                    className={cn(
                      'tw:flex tw:items-center tw:gap-2',
                      item.checked && 'tw:text-muted-foreground tw:line-through'
                    )}
                  >
                    <Checkbox checked={item.checked} readOnly />
                    {item.label}
                  </label>
                </li>
              ))}
            </ul>
          )}
          {(startDate || targetDate) && (
            <div className="tw:text-xs tw:text-muted-foreground">
              {startDate && <span>Start: {startDate}</span>}
              {startDate && targetDate && <span> · </span>}
              {targetDate && <span>Due: {targetDate}</span>}
            </div>
          )}
          {!action.description &&
            checklist.length === 0 &&
            !startDate &&
            !targetDate && (
              <div className="tw:text-muted-foreground">No details.</div>
            )}
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
};

export default EventActionRow;
